const DEFAULT_TEXT = { r: 0, g: 0, b: 0 };
const DEFAULT_FILL = { r: 1, g: 1, b: 1 };

const luminance = (r, g, b) => 0.2126 * r + 0.7152 * g + 0.0722 * b;

/**
 * Estimate text ink and local background from a rendered PDF canvas region.
 */
export const sampleTextAndFillColors = (canvasContext, box) => {
    if (!canvasContext || !box) {
        return { textColor: DEFAULT_TEXT, fillColor: DEFAULT_FILL };
    }

    const canvas = canvasContext.canvas;
    const left = Math.max(0, Math.floor(box.left));
    const top = Math.max(0, Math.floor(box.top));
    const width = Math.min(canvas.width - left, Math.ceil(box.width));
    const height = Math.min(canvas.height - top, Math.ceil(box.height));

    if (width < 2 || height < 2) {
        return { textColor: DEFAULT_TEXT, fillColor: DEFAULT_FILL };
    }

    let imageData;
    try {
        imageData = canvasContext.getImageData(left, top, width, height);
    } catch {
        return { textColor: DEFAULT_TEXT, fillColor: DEFAULT_FILL };
    }

    const data = imageData.data;
    const bgSamples = [];
    const fgSamples = [];
    let darkestPixel = null;
    let darkestLum = Infinity;

    for (let i = 0; i < data.length; i += 4) {
        const a = data[i + 3];
        if (a < 16) continue;
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const lum = luminance(r, g, b);
        if (lum > 235) {
            bgSamples.push([r, g, b]);
        } else if (lum < 200) {
            fgSamples.push([r, g, b]);
        }
        if (lum >= 28 && lum < 220 && lum < darkestLum) {
            darkestLum = lum;
            darkestPixel = [r, g, b];
        }
    }

    if (!fgSamples.length && darkestPixel) {
        fgSamples.push(darkestPixel);
    }

    const average = (samples, fallback) => {
        if (!samples.length) return fallback;
        const sum = samples.reduce(
            (acc, [r, g, b]) => {
                acc[0] += r;
                acc[1] += g;
                acc[2] += b;
                return acc;
            },
            [0, 0, 0]
        );
        const n = samples.length;
        return {
            r: sum[0] / n / 255,
            g: sum[1] / n / 255,
            b: sum[2] / n / 255,
        };
    };

    return {
        textColor: average(fgSamples, DEFAULT_TEXT),
        fillColor: average(bgSamples, DEFAULT_FILL),
    };
};

export const colorToCss = (color) =>
    `rgb(${Math.round(color.r * 255)}, ${Math.round(color.g * 255)}, ${Math.round(color.b * 255)})`;

export const rgbToHex = (color = {}) => {
    const r = Math.round(Math.max(0, Math.min(1, Number(color.r) || 0)) * 255);
    const g = Math.round(Math.max(0, Math.min(1, Number(color.g) || 0)) * 255);
    const b = Math.round(Math.max(0, Math.min(1, Number(color.b) || 0)) * 255);
    return `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
};

export const hexToNormalizedRgb = (hex = "#000000") => {
    const normalized = String(hex || "").trim().replace("#", "");
    if (normalized.length !== 6) {
        return { r: 0, g: 0, b: 0 };
    }
    const r = parseInt(normalized.slice(0, 2), 16);
    const g = parseInt(normalized.slice(2, 4), 16);
    const b = parseInt(normalized.slice(4, 6), 16);
    if ([r, g, b].some((v) => Number.isNaN(v))) {
        return { r: 0, g: 0, b: 0 };
    }
    return { r: r / 255, g: g / 255, b: b / 255 };
};
